import { css, html, LitElement } from "lit";
import { customElement } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import "../components/theme-switcher.ts";
import "../components/ui/iris-icon.ts";

/**
 * Two-column shell for signed-out pages: brand on the left, form on the right.
 *
 * The brand panel keeps a light surface in every theme. The logo's wordmark is
 * near-black with a transparent background, so on a dark surface it would
 * disappear while the coloured figure stayed visible.
 *
 * Every duration is a --dur-* token or multiplies --motion-scale, so the
 * ambient motion switches off with the user's reduced-motion setting.
 */
@localized()
@customElement("iris-auth-layout")
export class IrisAuthLayout extends LitElement {
  static styles = css`
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }
    :host {
      display: block;
      min-height: 100svh;
      background: var(--background);
    }
    .split {
      display: grid;
      grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
      min-height: 100svh;
    }

    /* ── Brand panel ──────────────────────────────────────────── */
    .brand {
      position: relative;
      overflow: hidden;
      isolation: isolate;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--space-6);
      padding: var(--space-10) var(--space-8);
      /* Fixed light surface: see the class comment above. */
      background: #f6f9fb;
      border-right: 1px solid rgba(15, 23, 42, 0.06);
    }
    /* Ambient colour drawn from the logo's wings, kept soft so the artwork
      stays the focal point. */
    .blob {
      position: absolute;
      z-index: -1;
      border-radius: 50%;
      filter: blur(70px);
      opacity: 0.55;
      will-change: transform;
      animation: drift calc(22s * var(--motion-scale)) ease-in-out infinite
        alternate;
    }
    .blob.green {
      width: 42vmin;
      height: 42vmin;
      top: -8%;
      left: -6%;
      background: #34d399;
    }
    .blob.blue {
      width: 36vmin;
      height: 36vmin;
      bottom: -10%;
      right: -4%;
      background: #60a5fa;
      animation-delay: calc(-7s * var(--motion-scale));
    }
    .blob.violet {
      width: 28vmin;
      height: 28vmin;
      top: 55%;
      left: 12%;
      background: #a78bfa;
      animation-delay: calc(-14s * var(--motion-scale));
    }
    @keyframes drift {
      from {
        transform: translate3d(0, 0, 0) scale(1);
      }
      to {
        transform: translate3d(6vmin, -5vmin, 0) scale(1.12);
      }
    }
    /* Fine grid gives the panel texture without competing with the logo. */
    .grid {
      position: absolute;
      inset: 0;
      z-index: -1;
      background-image:
        linear-gradient(rgba(15, 23, 42, 0.05) 1px, transparent 1px),
        linear-gradient(90deg, rgba(15, 23, 42, 0.05) 1px, transparent 1px);
      background-size: 32px 32px;
      mask-image: radial-gradient(
        ellipse at center,
        rgba(0, 0, 0, 0.6),
        transparent 75%
      );
    }

    .brand > :not(.blob):not(.grid) {
      opacity: 0;
      animation: rise var(--dur-glacial) var(--ease-1) forwards;
      animation-delay: calc(var(--i, 0) * 90ms * var(--motion-scale));
    }
    @keyframes rise {
      from {
        opacity: 0;
        transform: translateY(14px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .logo-wrap {
      animation:
        rise var(--dur-glacial) var(--ease-1) forwards,
        float calc(7s * var(--motion-scale)) ease-in-out
        calc(var(--dur-glacial) + 300ms) infinite;
    }
    .logo-wrap img {
      width: min(400px, 72%);
      height: auto;
      display: block;
      margin-inline: auto;
      animation: glow calc(5s * var(--motion-scale)) ease-in-out infinite;
    }
    @keyframes glow {
      0%,
      100% {
        filter: drop-shadow(0 14px 26px rgba(16, 185, 129, 0.16));
      }
      50% {
        filter: drop-shadow(0 22px 40px rgba(59, 130, 246, 0.28));
      }
    }
    @keyframes float {
      0%,
      100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-7px);
      }
    }

    .headline {
      margin: 0;
      max-width: 22ch;
      text-align: center;
      color: #0f172a;
      font-size: clamp(1.5rem, 2.4vw, 2rem);
      font-weight: 600;
      letter-spacing: -0.01em;
      line-height: 1.2;
    }
    .tagline {
      margin: 0;
      max-width: 40ch;
      text-align: center;
      color: #475569;
      font-size: var(--text-base);
      line-height: 1.6;
    }
    .pills {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: var(--space-2);
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .pills li {
      display: inline-flex;
      align-items: center;
      gap: var(--space-1-5);
      padding: var(--space-1-5) var(--space-3);
      border-radius: var(--radius-full);
      background: rgba(255, 255, 255, 0.7);
      border: 1px solid rgba(15, 23, 42, 0.08);
      color: #334155;
      font-size: var(--text-sm);
      backdrop-filter: blur(6px);
    }
    .pills iris-icon {
      color: #059669;
    }
    .pills li {
      animation: pill-tour calc(8s * var(--motion-scale)) ease-in-out infinite;
      animation-delay: calc(var(--n, 0) * 2s * var(--motion-scale));
    }
    @keyframes pill-tour {
      0%,
      18%,
      100% {
        background: rgba(255, 255, 255, 0.7);
        border-color: rgba(15, 23, 42, 0.08);
        transform: translateY(0);
      }
      6%,
      12% {
        background: #ffffff;
        border-color: rgba(5, 150, 105, 0.45);
        box-shadow: 0 8px 20px -10px rgba(5, 150, 105, 0.5);
        transform: translateY(-2px);
      }
    }

    /* ── Mini kanban: one card travels across the columns on a loop ── */
    .board {
      --col: 96px;
      --gap: 12px;
      position: relative;
      display: grid;
      grid-template-columns: repeat(3, var(--col));
      gap: var(--gap);
      padding: var(--space-3);
      border-radius: var(--radius-xl);
      background: rgba(255, 255, 255, 0.72);
      border: 1px solid rgba(15, 23, 42, 0.08);
      box-shadow: 0 20px 40px -24px rgba(15, 23, 42, 0.25);
      backdrop-filter: blur(8px);
    }
    .col {
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-height: 118px;
    }
    .col-title {
      display: flex;
      align-items: center;
      gap: 5px;
      margin-bottom: 2px;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #64748b;
    }
    .col-title i {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }
    .col:nth-child(1) .col-title {
      color: #64748b;
    }
    .col:nth-child(2) .col-title {
      color: #2563eb;
    }
    .col:nth-child(3) .col-title {
      color: #059669;
    }
    .ghost {
      height: 26px;
      border-radius: 6px;
      background: rgba(15, 23, 42, 0.05);
      border: 1px solid rgba(15, 23, 42, 0.05);
    }
    .ghost.short {
      width: 70%;
    }
    /* Reserve the hero card's slot so columns don't reflow as it moves. */
    .slot {
      height: 34px;
    }
    .hero {
      position: absolute;
      top: calc(var(--space-3) + 20px);
      left: var(--space-3);
      width: var(--col);
      height: 34px;
      padding: 6px 7px;
      border-radius: 7px;
      background: #ffffff;
      border: 1px solid rgba(15, 23, 42, 0.1);
      box-shadow: 0 10px 20px -12px rgba(15, 23, 42, 0.35);
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      animation: travel calc(9s * var(--motion-scale)) var(--ease-2) infinite;
    }
    .hero .line {
      height: 5px;
      width: 78%;
      border-radius: 3px;
      background: #cbd5e1;
    }
    .hero .bar {
      height: 4px;
      border-radius: 2px;
      background: rgba(15, 23, 42, 0.08);
      overflow: hidden;
    }
    .hero .bar::after {
      content: "";
      display: block;
      height: 100%;
      width: 0;
      background: linear-gradient(90deg, #34d399, #3b82f6);
      animation: fill calc(9s * var(--motion-scale)) var(--ease-2) infinite;
    }
    .hero .tick {
      position: absolute;
      top: -6px;
      right: -6px;
      display: grid;
      place-items: center;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #059669;
      color: #fff;
      box-shadow: 0 0 0 2px #fff;
      animation: tick calc(9s * var(--motion-scale)) var(--ease-1) infinite;
    }
    @keyframes travel {
      0% {
        opacity: 0;
        transform: translate(0, 6px) scale(0.96);
      }
      5% {
        opacity: 1;
        transform: translate(0, 0) scale(1);
      }
      30% {
        transform: translate(0, 0);
      }
      40% {
        transform: translate(calc(var(--col) + var(--gap)), 0);
      }
      62% {
        transform: translate(calc(var(--col) + var(--gap)), 0);
      }
      72% {
        transform: translate(calc(2 * (var(--col) + var(--gap))), 0);
      }
      90% {
        opacity: 1;
        transform: translate(calc(2 * (var(--col) + var(--gap))), 0);
      }
      97%,
      100% {
        opacity: 0;
        transform: translate(calc(2 * (var(--col) + var(--gap))), -6px);
      }
    }
    @keyframes fill {
      0%,
      5% {
        width: 0;
      }
      40% {
        width: 35%;
      }
      62% {
        width: 60%;
      }
      74%,
      100% {
        width: 100%;
      }
    }
    @keyframes tick {
      0%,
      72% {
        opacity: 0;
        transform: scale(0.4);
      }
      78% {
        opacity: 1;
        transform: scale(1.15);
      }
      82%,
      90% {
        opacity: 1;
        transform: scale(1);
      }
      97%,
      100% {
        opacity: 0;
        transform: scale(1);
      }
    }

    /* ── Form side ─────────────────────────────────────────────── */
    .form-side {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--space-6) var(--space-4);
    }
    .theme {
      position: absolute;
      top: var(--space-4);
      right: var(--space-4);
    }

    @media (max-width: 860px) {
      .split {
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr;
      }
      .brand {
        gap: var(--space-3);
        padding: var(--space-8) var(--space-4) var(--space-6);
        border-right: none;
        border-bottom: 1px solid rgba(15, 23, 42, 0.06);
      }
      .logo-wrap img {
        width: min(240px, 62%);
      }
      .headline {
        font-size: var(--text-xl);
      }
      .tagline {
        font-size: var(--text-sm);
      }
      .pills,
      .board {
        display: none;
      }
      .blob {
        opacity: 0.4;
      }
    }
  `;

  protected render() {
    return html`
      <div class="split">
        <aside class="brand">
          <div class="blob green"></div>
          <div class="blob blue"></div>
          <div class="blob violet"></div>
          <div class="grid"></div>

          <div class="logo-wrap" style="--i:0">
            <img src="/logo.png" alt="IRIS" />
          </div>
          <h2 class="headline" style="--i:1">
            ${msg("Plan the work. See it move.")}
          </h2>
          <p class="tagline" style="--i:2">
            ${msg(
              "Project management for teams: projects, tasks, cycles and dashboards in one calm workspace.",
            )}
          </p>
          <div class="board" style="--i:3" aria-hidden="true">
            <div class="col">
              <div class="col-title"><i></i>${msg("To do")}</div>
              <div class="slot"></div>
              <div class="ghost"></div>
              <div class="ghost short"></div>
            </div>
            <div class="col">
              <div class="col-title"><i></i>${msg("In progress")}</div>
              <div class="slot"></div>
              <div class="ghost short"></div>
            </div>
            <div class="col">
              <div class="col-title"><i></i>${msg("Done")}</div>
              <div class="slot"></div>
              <div class="ghost"></div>
              <div class="ghost"></div>
            </div>
            <div class="hero">
              <span class="line"></span>
              <span class="bar"></span>
              <span class="tick">
                <iris-icon name="check" size="10"></iris-icon>
              </span>
            </div>
          </div>
          <ul class="pills" style="--i:4">
            <li style="--n:0">
              <iris-icon name="folder-kanban" size="14"></iris-icon>
              ${msg("Kanban & lists")}
            </li>
            <li style="--n:1">
              <iris-icon name="calendar-days" size="14"></iris-icon>
              ${msg("Cycles")}
            </li>
            <li style="--n:2">
              <iris-icon name="chart-bar" size="14"></iris-icon>
              ${msg("Dashboards")}
            </li>
            <li style="--n:3">
              <iris-icon name="clock" size="14"></iris-icon>
              ${msg("Time tracking")}
            </li>
          </ul>
        </aside>

        <main class="form-side">
          <div class="theme">
            <iris-theme-switcher></iris-theme-switcher>
          </div>
          <slot></slot>
        </main>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "iris-auth-layout": IrisAuthLayout;
  }
}
